import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Stack, HStack, Button, useToast } from "@chakra-ui/react";
import {
  getLibrariesData,
  updateLibrariesData,
} from "../../../services/Libraries";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import FormLoader from "../../../CommonComponents/FormLoader";
import { useSelector } from "react-redux";
import { handleApiError, showSuccessToast } from "../../../util/errorHandler";

// Left column schema
const leftColumnSchema = {
  title: "",
  type: "object",
  properties: {
    _id: { type: "string" },
    volume: {
      type: "string",
      title: "Volume",
    },
    volume_number: {
      type: "string",
      title: "Volume number",
    },
    inscription: {
      type: "string",
      title: "Inscription",
    },
    inside_image: {
      type: "array",
      title: "Inside image",
      items: { type: "string" },
    },
    isbn: {
      type: "string",
      title: "ISBN",
    },
    isbn_issn: {
      type: "string",
      title: "ISBN ISSN",
    },
    issn: {
      type: "string",
      title: "ISSN",
    },
    issue: {
      type: "string",
      title: "Issue",
    },
    issue_number: {
      type: "string",
      title: "Issue number",
    },
    language_of_publication: {
      type: "string",
      title: "Language of publication",
    },
    language_of_publication_alias: {
      type: "string",
      title: "Language of publication alias",
    },
    country_of_publication: {
      type: "string",
      title: "Country of publication",
    },
    binding_status: {
      type: "string",
      title: "Binding status",
    },
    date_and_month_of_publication: {
      type: "string",
      title: "Date and month of publication",
    },
    date_of_first_edition: {
      type: "string",
      title: "Date of first edition",
    },
    date_of_last_entry: {
      type: "string",
      title: "Date of last entry",
    },
    date_of_published_edition: {
      type: "string",
      title: "Date of published edition",
    },
    book_age: {
      type: "number",
      title: "Book age",
    },
    pagination: {
      type: "string",
      title: "Precise Pagination in Publication",
    },
    age: {
      type: "string",
      title: "Age",
    },
  },
};

// Right column schema
const rightColumnSchema = {
  title: "",
  type: "object",
  properties: {
    date_of_published_edition_in_library: {
      type: "string",
      title: "Date of published edition in library",
    },
    dates_of_first_edition: {
      type: "string",
      title: "Dates of first edition",
    },
    description: {
      type: "string",
      title: "Description",
    },
    index_image_alias: {
      type: "string",
      title: "Index image alias",
    },
    content_data: {
      type: "string",
      title: "Content data",
    },
    index_data: {
      type: "string",
      title: "Index data",
    },
    about_book: {
      type: "string",
      title: "About book",
    },
    about_author: {
      type: "string",
      title: "About author",
    },
    about_the_author: {
      type: "string",
      title: "About the author",
    },
    about_the_publication: {
      type: "string",
      title: "About the publication",
    },
    contents_text: {
      type: "string",
      title: "Contents text",
    },
    edition: {
      type: "string",
      title: "Edition Number",
    },
    edition_alias: {
      type: "string",
      title: "Edition alias",
    },
    place_of_publication: {
      type: "string",
      title: "Place of publication",
    },
    place_of_publication_artwork: {
      type: "string",
      title: "Place of Publication or Artwork Creation",
    },
    provenance: {
      type: "string",
      title: "Provenance",
    },
    published_in: {
      type: "string",
      title: "Published in",
    },
    publisher_alias: {
      type: "string",
      title: "Publisher alias",
    },
    publisher: {
      type: "string",
      title: "Publisher",
    },
  },
};

// Combined schema for preview mode
const combinedSchema = {
  title: "",
  description: "",
  type: "object",
  properties: {
    _id: { type: "string" },
    volume: {
      type: "string",
      title: "Volume",
    },
    volume_number: {
      type: "string",
      title: "Volume number",
    },
    inscription: {
      type: "string",
      title: "Inscription",
    },
    inside_image: {
      type: "array",
      title: "Inside image",
      items: { type: "string" },
    },
    isbn: {
      type: "string",
      title: "ISBN",
    },
    isbn_issn: {
      type: "string",
      title: "ISBN ISSN",
    },
    issn: {
      type: "string",
      title: "ISSN",
    },
    issue: {
      type: "string",
      title: "Issue",
    },
    issue_number: {
      type: "string",
      title: "Issue number",
    },
    language_of_publication: {
      type: "string",
      title: "Language of publication",
    },
    language_of_publication_alias: {
      type: "string",
      title: "Language of publication alias",
    },
    country_of_publication: {
      type: "string",
      title: "Country of publication",
    },
    binding_status: {
      type: "string",
      title: "Binding status",
    },
    date_and_month_of_publication: {
      type: "string",
      title: "Date and month of publication",
    },
    date_of_first_edition: {
      type: "string",
      title: "Date of first edition",
    },
    date_of_last_entry: {
      type: "string",
      title: "Date of last entry",
    },
    date_of_published_edition: {
      type: "string",
      title: "Date of published edition",
    },
    book_age: {
      type: "number",
      title: "Book age",
    },
    pagination: {
      type: "string",
      title: "Precise Pagination in Publication",
    },
    age: {
      type: "string",
      title: "Age",
    },
    date_of_published_edition_in_library: {
      type: "string",
      title: "Date of published edition in library",
    },
    dates_of_first_edition: {
      type: "string",
      title: "Dates of first edition",
    },
    description: {
      type: "string",
      title: "Description",
    },
    index_image_alias: {
      type: "string",
      title: "Index image alias",
    },
    content_data: {
      type: "string",
      title: "Content data",
    },
    index_data: {
      type: "string",
      title: "Index data",
    },
    about_book: {
      type: "string",
      title: "About book",
    },
    about_author: {
      type: "string",
      title: "About author",
    },
    about_the_author: {
      type: "string",
      title: "About the author",
    },
    about_the_publication: {
      type: "string",
      title: "About the publication",
    },
    contents_text: {
      type: "string",
      title: "Contents text",
    },
    edition: {
      type: "string",
      title: "Edition Number",
    },
    edition_alias: {
      type: "string",
      title: "Edition alias",
    },
    place_of_publication: {
      type: "string",
      title: "Place of publication",
    },
    place_of_publication_artwork: {
      type: "string",
      title: "Place of Publication or Artwork Creation",
    },
    provenance: {
      type: "string",
      title: "Provenance",
    },
    published_in: {
      type: "string",
      title: "Published in",
    },
    publisher_alias: {
      type: "string",
      title: "Publisher alias",
    },
    publisher: {
      type: "string",
      title: "Publisher",
    },
  },
};

// Left column UI schema
const leftColumnUiSchema = {
  "ui:submitButtonOptions": { norender: true },
  _id: { "ui:widget": "hidden" },
  "ui:style": {
    fontSize: "14px",
  },
  volume: { "ui:emptyValue": "" },
  volume_number: { "ui:widget": "hidden" },
  inscription: { "ui:emptyValue": "" },
  inside_image: { "ui:emptyValue": [] },
  isbn: { "ui:emptyValue": "" },
  isbn_issn: { "ui:emptyValue": "" },
  issn: { "ui:emptyValue": "" },
  issue: { "ui:emptyValue": "" },
  issue_number: { "ui:emptyValue": "" },
  language_of_publication: { "ui:emptyValue": "" },
  language_of_publication_alias: { "ui:widget": "hidden" },
  country_of_publication: { "ui:emptyValue": "" },
  binding_status: { "ui:emptyValue": "" },
  date_and_month_of_publication: { "ui:emptyValue": "" },
  date_of_first_edition: { "ui:emptyValue": "" },
  date_of_last_entry: { "ui:emptyValue": "" },
  date_of_published_edition: { "ui:emptyValue": "" },
  book_age: { "ui:widget": "hidden" },
  pagination: { "ui:emptyValue": "" },
  age: { "ui:widget": "hidden" },
};

// Right column UI schema
const rightColumnUiSchema = (hasPublisherAlias) => ({
  "ui:submitButtonOptions": { norender: true },
  "ui:style": {
    fontSize: "14px",
  },
  date_of_published_edition_in_library: { "ui:emptyValue": "" },
  dates_of_first_edition: { "ui:emptyValue": "" },
  description: { "ui:emptyValue": "", "ui:widget": "textarea" },
  index_image_alias: { "ui:emptyValue": "" },
  content_data: { "ui:emptyValue": "" },
  index_data: { "ui:emptyValue": "" },
  about_book: { "ui:emptyValue": "" },
  about_author: { "ui:emptyValue": "" },
  about_the_author: { "ui:emptyValue": "" },
  about_the_publication: { "ui:emptyValue": "" },
  contents_text: { "ui:emptyValue": "" },
  edition: { "ui:emptyValue": "" },
  edition_alias: { "ui:widget": "hidden" },
  place_of_publication: { "ui:emptyValue": "" },
  place_of_publication_artwork: { "ui:emptyValue": "" },
  provenance: { "ui:emptyValue": "", "ui:widget": "textarea" },
  published_in: { "ui:emptyValue": "" },
  publisher_alias: { "ui:emptyValue": "" },
  // publisher is shown only when publisher_alias has a value
  publisher: {
    "ui:emptyValue": "",
    ...(hasPublisherAlias ? {} : { "ui:widget": "hidden" }),
  },
});

// Combined UI schema for preview mode
const combinedUiSchema = (hasPublisherAlias) => ({
  "ui:submitButtonOptions": { norender: true },
  _id: { "ui:widget": "hidden" },
  "ui:style": {
    fontSize: "14px",
  },
  volume: { "ui:emptyValue": "" },
  volume_number: { "ui:widget": "hidden" },
  inscription: { "ui:emptyValue": "" },
  inside_image: { "ui:emptyValue": [] },
  isbn: { "ui:emptyValue": "" },
  isbn_issn: { "ui:emptyValue": "" },
  issn: { "ui:emptyValue": "" },
  issue: { "ui:emptyValue": "" },
  issue_number: { "ui:emptyValue": "" },
  language_of_publication: { "ui:emptyValue": "" },
  language_of_publication_alias: { "ui:widget": "hidden" },
  country_of_publication: { "ui:emptyValue": "" },
  binding_status: { "ui:emptyValue": "" },
  date_and_month_of_publication: { "ui:emptyValue": "" },
  date_of_first_edition: { "ui:emptyValue": "" },
  date_of_last_entry: { "ui:emptyValue": "" },
  date_of_published_edition: { "ui:emptyValue": "" },
  book_age: { "ui:widget": "hidden" },
  pagination: { "ui:emptyValue": "" },
  age: { "ui:widget": "hidden" },
  date_of_published_edition_in_library: { "ui:emptyValue": "" },
  dates_of_first_edition: { "ui:emptyValue": "" },
  description: { "ui:emptyValue": "", "ui:widget": "textarea" },
  index_image_alias: { "ui:emptyValue": "" },
  content_data: { "ui:emptyValue": "" },
  index_data: { "ui:emptyValue": "" },
  about_book: { "ui:emptyValue": "" },
  about_author: { "ui:emptyValue": "" },
  about_the_author: { "ui:emptyValue": "" },
  about_the_publication: { "ui:emptyValue": "" },
  contents_text: { "ui:emptyValue": "" },
  edition: { "ui:emptyValue": "" },
  edition_alias: { "ui:widget": "hidden" },
  place_of_publication: { "ui:emptyValue": "" },
  place_of_publication_artwork: { "ui:emptyValue": "" },
  provenance: { "ui:emptyValue": "", "ui:widget": "textarea" },
  published_in: { "ui:emptyValue": "" },
  publisher_alias: { "ui:emptyValue": "" },
  // publisher is shown only when publisher_alias has a value
  publisher: {
    "ui:emptyValue": "",
    ...(hasPublisherAlias ? {} : { "ui:widget": "hidden" }),
  },
});

const BookInfo = () => {
  const { alias } = useParams();
  const [quickSave, setQuickSave] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector((state) => state.preview.isEnabled);
  const toast = useToast();
  const type = window.location.pathname.split("/")[1];

  const hasValue = (value) => !!(value && String(value).trim() !== "");

  // Clear publisher when publisher_alias is cleared, so a hidden stale value is not saved
  const syncPublisher = (next, prev) => {
    if (hasValue(prev?.publisher_alias) && !hasValue(next.publisher_alias)) {
      next.publisher = "";
    }
    return next;
  };

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = [
      "_id",
      "volume",
      "volume_number",
      "inscription",
      "inside_image",
      "isbn",
      "isbn_issn",
      "issn",
      "issue",
      "issue_number",
      "language_of_publication",
      "language_of_publication_alias",
      "country_of_publication",
      "binding_status",
      "date_and_month_of_publication",
      "date_of_first_edition",
      "date_of_last_entry",
      "date_of_published_edition",
      "book_age",
      "pagination",
      "age",
    ];
    const rightFields = [
      "date_of_published_edition_in_library",
      "dates_of_first_edition",
      "description",
      "index_image_alias",
      "content_data",
      "index_data",
      "about_book",
      "about_author",
      "about_the_author",
      "about_the_publication",
      "contents_text",
      "edition",
      "edition_alias",
      "place_of_publication",
      "place_of_publication_artwork",
      "provenance",
      "published_in",
      "publisher_alias",
      "publisher",
    ];

    const leftData = {};
    const rightData = {};

    leftFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });

    rightFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });

    return { leftData, rightData };
  };

  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(syncPublisher({ ...data.formData }, rightFormData));
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(syncPublisher({ ...data.formData }, combinedFormData));
  };

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getLibrariesData(alias, "book-info", type);
          if (result) {
            const raw = result?.data?.[0];
            const data = {
              ...raw,
              inside_image: Array.isArray(raw?.inside_image)
                ? raw.inside_image
                : raw?.inside_image
                  ? raw.inside_image
                      .split("|")
                      .map((s) => s.trim())
                      .filter(Boolean)
                  : [],
            };
            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(data);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(data);
          }
        } else {
          // For new records, initialize empty forms
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data",
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      let finalFormData;

      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }

      // Convert inside_image array back to string for the backend
      if (Array.isArray(finalFormData.inside_image)) {
        finalFormData = {
          ...finalFormData,
          inside_image: finalFormData.inside_image.filter(Boolean).join(" | "),
        };
      }

      // Remove _id from create operations
      if (!alias && finalFormData._id) {
        delete finalFormData._id;
      }

      if (alias) {
        const result = await updateLibrariesData(
          alias,
          finalFormData,
          "",
          "book-info",
        );

        if (result?.status === 200) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Box
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                p={4}
                borderRadius="md"
              >
                <Box fontSize="lg" fontWeight="bold" mb={4}>
                  Book Info Fields
                </Box>
                <Form
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema(
                    hasValue(combinedFormData?.publisher_alias),
                  )}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  fontsize={"14px"}
                />
              </Box>
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Stack
                flexDirection="row"
                gap={4}
                alignItems="flex-start"
                flex={1}
              >
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Box fontSize="lg" fontWeight="bold" mb={4}>
                      Book Info Fields
                    </Box>
                    <Form
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema(
                        hasValue(rightFormData?.publisher_alias),
                      )}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default BookInfo;
